/**
 * Application configuration scattered no more — every tunable value, feature
 * flag and static option list lives here, except Auth0 (`auth.config.ts`) and
 * Sentry (`sentry.config.ts`), which stay isolated since they're already
 * self-contained, validated, and don't have the duplication problem this file
 * exists to solve. Values scoped to a single component's own rendering
 * (waveform geometry, popover offsets, PDF layout) also stay local to that
 * component — only genuinely shared or previously-duplicated config moved here.
 */

const validateEnvVar = (name: string, value: string | undefined): string => {
  if (!value) throw new Error(`Missing required environment variable: ${name}`);
  return value;
};

// --- Backend API ---
export const API_URL = validateEnvVar('VITE_API_URL', import.meta.env.VITE_API_URL);
export const API_KEY = validateEnvVar('VITE_API_KEY', import.meta.env.VITE_API_KEY);
// --- Chat ---
export const TOKEN_CONTEXT_LIMIT = parseInt(import.meta.env.VITE_TOKEN_CONTEXT_LIMIT ?? '350000', 10);
/** Conversations fetched per page from `/db/user-conversations` (max allowed by the backend is 100). */
export const HISTORY_PAGE_LIMIT = 25;
/**
 * How often streamed text is pushed into React state, in ms.
 *
 * SSE chunks arrive at network speed — word-level events, a few hundred per
 * response. Dispatching each one re-rendered the tree at that same rate,
 * which starved the main thread (and made the auto-scroll stutter). Batching
 * into ~50ms slices means ~20 text updates/sec: still perfectly fluid to
 * read, an order of magnitude less work for React.
 */
export const STREAM_FLUSH_MS = 50;

// --- Speech-to-text (shared across assemblyai/elevenlabs providers) ---
export const STT_CONFIG = {
  /** AssemblyAI's Begin / ElevenLabs' session_started guard — never enter
   *  'listening' without this within the window, or we'd stream into the void. */
  beginTimeoutMs: 8000,
  /** No server ack exists for ElevenLabs' flush(); both providers fall back to
   *  this fixed wait before tearing down on stop(). */
  finalizeTimeoutMs: 2000,
  /** Hard per-session cap, both providers. */
  maxSessionMs: 60000,
  /** ~1s of audio at 16kHz/16-bit mono. Past this, the socket can't keep up
   *  with the mic in real time; dropping chunks degrades transcription
   *  quality but an unbounded bufferedAmount would otherwise stall the tab
   *  with no visible error. */
  maxBufferedBytes: 32000,
  /** AssemblyAI-only: proper nouns biased via keyterms_prompt (streaming v3 WS
   *  param) so they survive non-English transcription — e.g. "Phytobiotics"
   *  was coming back as "fitobiótica" in Portuguese. Max 100 terms/50 chars
   *  each per AssemblyAI's limit. No "®" — it's never spoken, so a term with
   *  it is a wasted near-duplicate of the bare name, not extra protection
   *  (AssemblyAI's own guidance: fewer, distinct terms beat exhaustive lists —
   *  https://www.assemblyai.com/blog/when-keyterm-prompting-backfires). Expand
   *  only with names actually failing in testing, not the full product catalog. */
  keytermsPrompt: [
    'Phytobiotics',
    'Sangrovit',
    'Sangrovit W',
    'Sangrovit Extra',
    'Sangrovit WS',
    'Sangrovit RS',
    'Sangrovit CS',
    'Sangrovit CSPX',
    'Sangrovit Extra x10',
    'Plexomin',
    'Plexomin Fe',
    'Plexomin Zn',
    'Plexomin Cu',
    'Plexomin Mn',
    'Plexomin Lys',
    'Active D',
    'Active D5',
    'Active D Conc',
    'Flavours and Sweeteners',
    'Immune Milk',
    'Dr. Phyto',
  ],
} as const;

// --- Auth retry / re-check cadence ---
export const AUTH_RETRY = {
  maxRetries: 3,
  resetIntervalMs: 60000,
} as const;
/** Tab-visibility silent re-check cooldown (catches account suspension). */
export const VISIBILITY_RECHECK_COOLDOWN_MS = 2 * 60 * 1000;

// --- PWA ---
export const PWA_INSTALL_PROMPT_DELAY_MS = 4000;

// --- localStorage keys (single source, avoids future collisions) ---
export const STORAGE_KEYS = {
  chatHistory: 'phyto.chat.history',
  tutorialSeen: 'phyto_has_seen_tutorial',
  pwaInstallDismissed: 'phyto_pwa_install_dismissed',
} as const;

// --- Feature flags — flip to `true` to enable before releasing ---
export const FEATURES = {
  /** User self-service profile page (/profile) */
  USER_PROFILE: true,
  /** "Edit user" action in the admin user table */
  ADMIN_EDIT_USER: true,
  /** Suggested query chips on the welcome screen (onboarding guidance) */
  ONBOARDING_GUIDANCE: true,
  /** Show "Frequent Queries" category in the chips variant */
  ONBOARDING_SHOW_FREQUENT_QUERIES: false,
  /** Use category-panel variant instead of flat chips */
  ONBOARDING_GUIDANCE_CATEGORIES: true,
  /** Analytics dashboard panel in the admin area */
  ANALYTICS_PANEL: true,
  /** 5-step coach marks tutorial for new users */
  ONBOARDING_TUTORIAL: true,
  /** Mic button + voice-to-text dictation in the message input */
  SPEECH_INPUT: true,
  /** Which speech-to-text backend the mic button uses. */
  SPEECH_PROVIDER: 'assemblyai' as 'assemblyai' | 'webspeech' | 'elevenlabs',
  /** Use mock data in the admin dashboard instead of the real /admin/* endpoints. */
  USE_ADMIN_MOCKS: false,

  /** Use mock data for feedback submission instead of the real backend. */
  USE_FEEDBACK_MOCK: false,
} as const;

// --- Static lists of options for user profile fields ---
export const LANGUAGES = [
  'English',
  'Spanish',
  'Portuguese',
  'German',
] as const;
export type Language = typeof LANGUAGES[number];

export const DIVISIONS = [
  'Acquaculture',
  'General',
  'In Vitro',
  'Pet',
  'Poultry',
  'Ruminant',
  'Swine',
] as const;
